import { createContext } from 'react';
import type { NormalizedError } from '@/api/errors';
import type { SessionResponse } from '@/api/types';

/**
 * The session lifecycle, as seen by the UI:
 *  - exchanging : trading the invite token for a session (show a calm loader)
 *  - active     : a live in-memory session — the ONLY state that renders authenticated views
 *  - gone       : the invite was already used / expired (410) — terminal
 *  - expired    : a live session hit a 401 mid-use, or timed out — reopen the link
 *  - no-link    : no invite token in the URL and no session — "please open your link"
 *  - error      : network / server / unknown — retryable
 */
export type SessionStatus =
  | 'exchanging'
  | 'active'
  | 'gone'
  | 'expired'
  | 'no-link'
  | 'error';

export interface SessionState {
  status: SessionStatus;
  /** The session metadata (caseRef, applicantLabel, locale, expiresAt) — NEVER the raw token. */
  session: SessionResponse | null;
  error: NormalizedError | null;
}

export interface SessionContextValue extends SessionState {
  /** Re-attempt the exchange from the invite token in the URL (for the error state). */
  retry: () => void;
}

export const SessionContext = createContext<SessionContextValue | null>(null);
